import type {
  AttachmentTranslationResponse,
  MediaArchiveGetSignedUrlResponse,
  MeetingEventLiveStreamResponse,
  SearchMeetingEventsQueryParams,
} from '@govinity/validation';
import type {
  MeetingCurrentLiveItemResponse,
  MeetingEventHistoryResponse,
  MeetingEventPublicGetAllResponse,
  MeetingEventPublicGetResponse,
  MeetingEventTimestampPublicResponse,
  MeetingEventYearsResponse,
  MeetingLiveStateResponse,
  MeetingTranscriptSearchBody,
  MeetingTranscriptSearchResponse,
} from '@govinity/validation/public';

import { ReadOnlyApi } from '../base';

export class MeetingsApi extends ReadOnlyApi<
  MeetingEventPublicGetAllResponse,
  MeetingEventPublicGetResponse,
  SearchMeetingEventsQueryParams
> {
  parentPath: string;
  governmentCode: string;

  constructor(governmentCode: string, parentPath: string) {
    super();

    this.parentPath = parentPath;
    this.governmentCode = governmentCode;
  }

  getPath() {
    return `${this.parentPath}/${this.governmentCode}/meetings`;
  }

  async requestDocumentTranslation(meetingId: number, documentId: number) {
    const { data } = await this.request<AttachmentTranslationResponse>({
      method: 'POST',
      url: `${this.getPath()}/${meetingId}/documents/${documentId}/translation`,
    });

    return data;
  }

  async getYears() {
    const { data } = await this.request<MeetingEventYearsResponse>({
      method: 'GET',
      url: `${this.getPath()}/years`,
    });

    return data;
  }

  async countWithAgenda() {
    const { data } = await this.request<{ count: number }>({
      method: 'GET',
      url: `${this.getPath()}/count-with-agenda`,
    });

    return data;
  }

  async getRecordingUrl(meetingId: number) {
    const { data } = await this.request<MediaArchiveGetSignedUrlResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/recording`,
    });

    return data;
  }

  async getLiveStreamUrl(meetingId: number) {
    const { data } = await this.request<MeetingEventLiveStreamResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/live-stream`,
    });

    return data;
  }

  async getLiveState(meetingId: number) {
    const { data } = await this.request<MeetingLiveStateResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/live-state`,
    });

    return data;
  }

  async getCurrentLiveItem(meetingId: number) {
    const { data } = await this.request<MeetingCurrentLiveItemResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/current-live-item`,
    });

    return data;
  }

  async getTimestamps(meetingId: number) {
    const { data } = await this.request<MeetingEventTimestampPublicResponse[]>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/timestamps`,
    });

    return data;
  }

  async transcriptSearch(body: MeetingTranscriptSearchBody) {
    const { data } = await this.request<MeetingTranscriptSearchResponse>({
      method: 'POST',
      url: `${this.getPath()}/transcript-search`,
      data: body,
    });

    return data;
  }

  async getHistory(meetingId: number) {
    const { data } = await this.request<MeetingEventHistoryResponse>({
      method: 'GET',
      url: `${this.getPath()}/${meetingId}/history`,
    });

    return data;
  }
}
